import { useEffect, useId, useRef, useState } from "react";
import { Divider, Stack, Tooltip, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { CoreCard, CoreChip, CoreRadioGroup, CoreSkeletonWrapper, RadioOption } from "@verity/components/core";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AiIcon from "../../../../portal/components/assessments-page/assets/ai-icon.svg?react";

import i18n from "../../../../i18n.js";
import styles from "./QuizResultPanel.module.css";
import { UseVerityAssessmentResultReturn } from "./hooks/useVerityAssessmentResult.js";
import {
	DraftGrade,
	getGradeDraftFromResponse,
	getMaxGradeForQuestion,
	isGradeable,
	QuizQuestion,
	QuizResponseResult,
} from "./hooks/verityAssessmentResultHelper.js";
import { GradeInput, NotesTextField } from "./tabs/AssignmentPanel.js";

import {
	BlankQuestionText,
	getScoreStatus,
	getStatusTooltip,
	QuestionTypeIcon,
	StatusIcon,
	t,
} from "./question-types/shared";
import { MCAnswerView, MCCorrectAnswer } from "./question-types/MCAnswerView";
import { FITBAnswerView, FITBCorrectAnswer } from "./question-types/FITBAnswerView";
import { DropdownAnswerView, DropdownCorrectAnswer } from "./question-types/DropdownAnswerView";
import { DragAndDropAnswerView, DragAndDropCorrectAnswer } from "./question-types/DragAndDropAnswerView";
import { MatchingAnswerView, MatchingCorrectAnswer } from "./question-types/MatchingAnswerView";
import { EssayAnswerView } from "./question-types/EssayAnswerView";
import { OrderingAnswerView, OrderingCorrectAnswer } from "./question-types/OrderingAnswerView";
import { CodingAnswerView } from "./question-types/CodingAnswerView";
import { SpreadsheetAnswerView, SpreadsheetCorrectAnswer } from "./question-types/SpreadsheetAnswerView";
import { AudioRecordingAnswerView } from "./question-types/AudioRecordingAnswerView";
import { FileSubmissionAnswerView } from "./question-types/FileSubmissionAnswerView";
import { CaretDownIcon } from "../../../../portal/components/interview-hub/ScheduleInterview/assets";

type QuizResultPanelProps = Pick<
	UseVerityAssessmentResultReturn,
	"quizQuestions" | "quizResponses" | "grade" | "loading"
>;

type GradeSectionProps = {
	response: QuizResponseResult;
	grade: DraftGrade;
} & Pick<QuestionDetailProps, "question" | "grade" | "onChange">;

function GradeSection({ grade, question, onChange, response }: GradeSectionProps) {
	const maxGrade = getMaxGradeForQuestion(question);
	const id = useId();
	const responseId = response.id;

	const radioOptions: RadioOption[] = [
		{
			value: String(maxGrade),
			label: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.correct", {
				points: maxGrade,
			}) as string,
		},
		{
			value: "0",
			label: i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.incorrect") as string,
		},
	];

	const handleGradeChange = (value: string) => onChange(responseId, "score", value);

	return (
		<div className={styles.gradeSection}>
			<div className={styles.gradeHeader}>
				<Typography id={id} className={styles.sectionLabel}>
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.gradeTitle")}
				</Typography>
				{response.isUngraded ? (
					<Typography className={styles.gradeDescription}>
						{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.gradeDescriptionForUngraded")}
					</Typography>
				) : (
					<Typography className={styles.gradeDescription}>
						{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.gradeDescription")}
					</Typography>
				)}
			</div>
			<div className={styles.gradeInputWrapper}>
				<CoreRadioGroup
					radioOptions={radioOptions}
					radioGroupAriaLabelledby={id}
					onChange={(_, value) => handleGradeChange(value)}
					selectedValue={grade.score}
					className={styles.gradeRadioGroup}
				/>
				<GradeInput
					ariaLabelledBy={id}
					value={grade.score}
					maxGrade={maxGrade}
					onChange={handleGradeChange}
					error={grade.error}
				/>
			</div>
			<NotesTextField value={grade.notes} onChange={(value) => onChange(responseId, "notes", value)} />
		</div>
	);
}

type QuestionDetailProps = {
	question: QuizQuestion;
	response: QuizResponseResult | undefined;
	index: number;
	grade: DraftGrade | undefined;
} & Pick<QuizResultPanelProps["grade"], "onChange">;

function QuestionDetail({ question, response, index, grade, onChange }: QuestionDetailProps) {
	const status = getScoreStatus(
		response?.score ?? 0,
		question.correctionPoints,
		response?.isAnswered ?? false,
		response?.isUngraded ?? false,
	);

	const typeLabel: string = t(`${question.questionType}.label`);

	const scoreColor =
		status === "unanswered"
			? "#e6e6e6"
			: status === "correct"
				? "#dbe7e1"
				: status === "partial"
					? "#faefdd"
					: "#f8dcd8";

	const renderAnswers = () => {
		switch (question.questionType) {
			case "MultipleChoice":
			case "TrueFalse":
				return <MCAnswerView question={question} response={response} />;
			case "FillInTheBlank":
				return <FITBAnswerView question={question} response={response} />;
			case "Matching":
				return <MatchingAnswerView question={question} response={response} />;
			case "Essay":
				return <EssayAnswerView response={response} />;
			case "Ordering":
				return <OrderingAnswerView question={question} response={response} />;
			case "DragAndDrop":
				return <DragAndDropAnswerView question={question} response={response} />;
			case "Dropdown":
				return <DropdownAnswerView question={question} response={response} />;
			case "Coding":
				return <CodingAnswerView response={response} />;
			case "Spreadsheet":
				return <SpreadsheetAnswerView question={question} response={response} />;
			case "AudioRecording":
				return <AudioRecordingAnswerView question={question} response={response} />;
			case "FileSubmission":
				return <FileSubmissionAnswerView response={response} />;
		}
	};

	const [isAiJustificationExpanded, setIsAiJustificationExpanded] = useState(false);
	const RenderAiGradingJustification = (justification: string, aiGradingCriteria: string) => {
		if (!justification) return null;
		return (
			<Stack gap="6px" my="20px">
				<Stack direction="row" alignItems="center" spacing="5px">
					<AiIcon color="#633BEE" />
					<Typography className={styles.sectionLabel}>{t("aiJustificationLabel")}</Typography>
				</Stack>
				<Stack gap="10px">
					{justification}
					<button
						type="button"
						aria-expanded={isAiJustificationExpanded}
						className={styles.viewGradingGuideline}
						onClick={() => setIsAiJustificationExpanded(!isAiJustificationExpanded)}
					>
						<Typography>{t("viewGradingGuideline")}</Typography>
						<CaretDownIcon
							width="16px"
							height="16px"
							color="#575757"
							style={{ transform: isAiJustificationExpanded ? "rotate(180deg)" : undefined }}
						/>
					</button>
					{isAiJustificationExpanded && <div>{aiGradingCriteria}</div>}
				</Stack>
			</Stack>
		);
	};

	const renderCorrectAnswer = () => {
		if (status === "correct") return null;
		switch (question.questionType) {
			case "MultipleChoice":
			case "TrueFalse":
				return <MCCorrectAnswer question={question} />;
			case "FillInTheBlank":
				return <FITBCorrectAnswer question={question} />;
			case "DragAndDrop":
				return <DragAndDropCorrectAnswer question={question} />;
			case "Dropdown":
				return <DropdownCorrectAnswer question={question} />;
			case "Matching":
				return <MatchingCorrectAnswer question={question} />;
			case "Ordering":
				return <OrderingCorrectAnswer question={question} />;
			case "Spreadsheet":
				return <SpreadsheetCorrectAnswer question={question} />;
		}
	};

	return (
		<CoreCard className={styles.detail} slotProps={{ bodyWrapper: { className: styles.questionDetailWrapper } }}>
			<div className={styles.detailHeader}>
				<div className={styles.detailHeaderLeft}>
					<Typography className={styles.questionTitle}>{t("questionLabel", { n: index + 1 })}</Typography>
					<Typography className={styles.questionTypeLabel}>{typeLabel}</Typography>
				</div>
				{response?.gradedByAi && (
					<div className={styles.aiGradingBadge}>
						<AiIcon color="white" height="18px" width="18px" />
						<Typography>{t("aiGradedBadge")}</Typography>
					</div>
				)}
				<div className={styles.spacer} />

				<CoreChip chipColor="#f8e4dd" variant="rectangular" sx={{ minWidth: "unset" }}>
					{t("pointsLabel", { count: question.correctionPoints })}
				</CoreChip>
				{response?.isUngraded ? (
					<CoreChip chipColor="#D3DDF2" variant="rectangular" sx={{ minWidth: "unset" }}>
						{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.ungradedLabel")}
					</CoreChip>
				) : (
					<CoreChip chipColor={scoreColor} variant="rectangular" sx={{ minWidth: "unset" }}>
						{t("scoredLabel", { count: response?.score ?? 0 })}
					</CoreChip>
				)}
			</div>

			<div className={styles.questionTextWrapper}>
				<Typography className={styles.sectionLabel}>{t("questionSection")}</Typography>
				{question.questionType === "FillInTheBlank" ||
				question.questionType === "DragAndDrop" ||
				question.questionType === "Dropdown" ? (
					<BlankQuestionText html={question.questionText} />
				) : (
					<Typography
						component="div"
						className={styles.questionText}
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.questionText) }}
					/>
				)}
			</div>

			<div className={styles.answerSection}>
				<Stack gap="10px">
					<Typography className={styles.sectionLabel}>{t("answerSection")}</Typography>
					{renderAnswers()}
				</Stack>
				{response?.gradedByAi &&
					RenderAiGradingJustification(response?.aiJustification || "", question.aiGradingCriteria ?? "")}
				{renderCorrectAnswer()}
				{response && isGradeable(question) && grade && (
					<>
						<Divider />
						<GradeSection grade={grade} onChange={onChange} question={question} response={response} />
					</>
				)}
			</div>
		</CoreCard>
	);
}

export function QuizResultPanel({ quizQuestions, quizResponses, grade, loading }: QuizResultPanelProps) {
	const [selectedIndex, setSelectedIndex] = useState(0);
	const navRef = useRef<HTMLDivElement>(null);
	const [showNavBtns, setShowNavBtns] = useState(false);

	// Reset to first question whenever a new session's questions are loaded
	useEffect(() => {
		setSelectedIndex(0);
	}, [quizQuestions]);

	// Show/hide scroll buttons depending on whether content overflows
	useEffect(() => {
		const el = navRef.current;
		if (!el) return;
		const observer = new ResizeObserver(() => {
			setShowNavBtns(el.scrollWidth > el.clientWidth);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [quizQuestions]);

	const scrollNav = (direction: "left" | "right") => {
		navRef.current?.scrollBy({ left: direction === "left" ? -150 : 150, behavior: "smooth" });
	};

	if (loading) {
		return (
			<div className={styles.panel}>
				<div className={styles.navBar}>
					<CoreSkeletonWrapper loading width="100%" height={56}>
						<div />
					</CoreSkeletonWrapper>
				</div>
				<div className={styles.detailScroll}>
					<CoreSkeletonWrapper loading height={400} width="calc(100% - 30px)" style={{ margin: "15px" }}>
						<div />
					</CoreSkeletonWrapper>
				</div>
			</div>
		);
	}

	if (quizQuestions.length === 0) {
		return (
			<div className={styles.emptyState}>
				<Typography variant="body2" color="text.secondary">
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.emptyState")}
				</Typography>
			</div>
		);
	}

	const selectedQuestion = quizQuestions[selectedIndex];
	const selectedResponse = quizResponses.find((r) => r.questionNo === selectedIndex + 1);
	const selectedGrade = getGradeDraftFromResponse(grade.drafts, selectedResponse);

	return (
		<div className={styles.panel}>
			{/* Question navigator bar */}
			<div className={styles.navBar}>
				{showNavBtns && (
					<button
						className={styles.navBtn}
						onClick={() => scrollNav("left")}
						aria-label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.scrollLeft") as string}
					>
						<ChevronLeftIcon fontSize="small" />
					</button>
				)}
				<div ref={navRef} className={styles.navScroll}>
					{quizQuestions.map((q, idx) => {
						const resp = quizResponses.find((r) => r.questionNo === idx + 1);
						const status = getScoreStatus(
							resp?.score ?? 0,
							q.correctionPoints,
							resp?.isAnswered ?? false,
							resp?.isUngraded ?? false,
						);
						const isSelected = idx === selectedIndex;
						return (
							<Tooltip key={idx} title={getStatusTooltip(status)} placement="top" arrow>
								<button
									className={`${styles.navItem}${isSelected ? ` ${styles.active}` : ""}`}
									onClick={() => setSelectedIndex(idx)}
									aria-label={
										i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.navItemAriaLabel", {
											n: idx + 1,
										}) as string
									}
									aria-pressed={isSelected}
								>
									<div className={styles.navIconWrapper}>
										<QuestionTypeIcon type={q.questionType} />

										<div className={styles.navStatusOverlay}>
											<StatusIcon status={status} isAiGraded={resp?.gradedByAi ?? false} />
										</div>
									</div>
									<Typography className={styles.navLabel}>
										{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.navLabel", {
											n: idx + 1,
										})}
									</Typography>
								</button>
							</Tooltip>
						);
					})}
				</div>
				{showNavBtns && (
					<button
						className={styles.navBtn}
						onClick={() => scrollNav("right")}
						aria-label={
							i18n.t("result.components.resultsPage.proctoringResultsViewTSX.questions.scrollRight") as string
						}
					>
						<ChevronRightIcon fontSize="small" />
					</button>
				)}
			</div>

			{/* Question detail */}
			<div className={styles.detailScroll}>
				{selectedQuestion != null && (
					<QuestionDetail
						key={selectedQuestion.id ?? selectedQuestion.questionOrder}
						question={selectedQuestion}
						response={selectedResponse}
						index={selectedIndex}
						grade={selectedGrade}
						onChange={grade.onChange}
					/>
				)}
			</div>
		</div>
	);
}
